import { z } from 'zod';

const idSchema = z.string().trim().min(1).max(120);
const isoTimestampSchema = z.string().datetime({ offset: true });
const calendarYmdSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const calendarYmSchema = z.string().regex(/^\d{4}-\d{2}$/);

const entryLaunchSchema = z.object({
  mode: z.enum(['income', 'expense']),
  categoryId: idSchema,
  accountId: idSchema.nullable(),
  cardId: idSchema.nullable(),
}).strict().refine((value) => (value.accountId === null) !== (value.cardId === null), 'Escolha uma conta ou cartão.');
export const openFinanceLaunchSchema = z.union([
  entryLaunchSchema,
  z.object({ mode: z.literal('transfer'), fromAccountId: idSchema, toAccountId: idSchema }).strict()
    .refine((value) => value.fromAccountId !== value.toAccountId, 'Escolha contas diferentes.'),
  z.object({ mode: z.literal('card_payment'), accountId: idSchema, cardId: idSchema, invoiceMonth: calendarYmSchema }).strict(),
]);
export type OpenFinanceLaunch = z.infer<typeof openFinanceLaunchSchema>;

export const syncOpenFinanceBodySchema = z.object({
  sourceId: idSchema.optional(),
}).strict();
export type SyncOpenFinanceBody = z.infer<typeof syncOpenFinanceBodySchema>;

export const syncOpenFinanceResponseSchema = z.object({
  runs: z.array(z.object({
    id: idSchema,
    sourceId: idSchema,
    status: z.enum(['succeeded', 'partial', 'failed']),
    fetched: z.number().int().nonnegative(),
    inserted: z.number().int().nonnegative(),
    deduplicated: z.number().int().nonnegative(),
    reviewItemsCreated: z.number().int().nonnegative(),
    error: z.string().max(300).nullable(),
  }).strict()).max(200),
}).strict();
export type SyncOpenFinanceResponse = z.infer<typeof syncOpenFinanceResponseSchema>;

export const openFinanceReviewItemSchema = z.object({
  id: idSchema,
  revision: z.number().int().positive(),
  unseen: z.boolean(),
  status: z.enum(['pending', 'approved', 'ignored', 'blocked', 'created', 'failed']),
  safe: z.boolean(),
  bulkSafe: z.boolean(),
  businessDate: calendarYmdSchema,
  suggestedDate: calendarYmdSchema,
  originalDescription: z.string().min(1).max(240),
  amountCents: z.number().int().safe(),
  providerStatus: z.string().min(1).max(80),
  previewStatus: z.enum(['existing', 'duplicate_suspected', 'candidate', 'special', 'review_required']),
  suggestedTitle: z.string().min(1).max(120),
  suggestedType: z.enum(['income', 'expense', 'transfer']).nullable(),
  suggestedCategoryId: idSchema.nullable(),
  suggestedCategoryName: z.string().min(1).max(120).nullable(),
  specialType: z.enum(['card_payment', 'transfer']).nullable(),
  counterpartyType: z.string().min(1).max(40).nullable(),
  counterpartyName: z.string().min(1).max(120).nullable(),
  operationType: z.string().min(1).max(80).nullable(),
  launch: openFinanceLaunchSchema.nullable(),
}).strict();
export type OpenFinanceReviewItem = z.infer<typeof openFinanceReviewItemSchema>;

export const openFinanceQueueResponseSchema = z.object({
  groups: z.array(z.object({
    connection: z.object({
      id: idSchema,
      provider: z.string().min(1).max(40),
      institutionName: z.string().min(1).max(120),
      status: z.enum(['pending_auth', 'active', 'refreshing', 'attention_required', 'consent_expired', 'disconnected']),
      lastSuccessfulSyncAt: isoTimestampSchema.nullable(),
    }).strict(),
    source: z.object({
      id: idSchema,
      kind: z.enum(['account', 'card']),
      displayName: z.string().min(1).max(120),
      mappedSourceId: idSchema,
    }).strict(),
    summary: z.object({
      pendingCount: z.number().int().nonnegative(),
      incomingCents: z.number().int().nonnegative(),
      outgoingCents: z.number().int().nonpositive(),
    }).strict(),
    items: z.array(openFinanceReviewItemSchema).max(200),
  }).strict()).max(200),
  syncIssues: z.array(z.object({
    sourceId: idSchema,
    sourceName: z.string().min(1).max(120),
    institutionName: z.string().min(1).max(120),
    status: z.enum(['partial', 'failed']),
    error: z.string().min(1).max(300),
    occurredAt: isoTimestampSchema,
  }).strict()).max(200),
}).strict();
export type OpenFinanceQueueResponse = z.infer<typeof openFinanceQueueResponseSchema>;

export const markOpenFinanceReviewSeenBodySchema = z.object({ revision: z.number().int().positive() }).strict();
export type MarkOpenFinanceReviewSeenBody = z.infer<typeof markOpenFinanceReviewSeenBodySchema>;

export const editOpenFinanceReviewBodySchema = z.object({
  revision: z.number().int().positive(),
  date: calendarYmdSchema,
  title: z.string().trim().min(1).max(120),
  launch: openFinanceLaunchSchema,
}).strict();
export type EditOpenFinanceReviewBody = z.infer<typeof editOpenFinanceReviewBodySchema>;

export const ignoreOpenFinanceReviewBodySchema = z.object({ revision: z.number().int().positive() }).strict();
export type IgnoreOpenFinanceReviewBody = z.infer<typeof ignoreOpenFinanceReviewBodySchema>;

export const approveOpenFinanceReviewBodySchema = z.object({
  revision: z.number().int().positive(),
  expectedDate: calendarYmdSchema,
  expectedTitle: z.string().trim().min(1).max(120),
  expectedCategoryId: idSchema.nullable(),
  expectedLaunch: openFinanceLaunchSchema,
  idempotencyKey: z.string().trim().min(8).max(120),
}).strict();
export type ApproveOpenFinanceReviewBody = z.infer<typeof approveOpenFinanceReviewBodySchema>;

const approvalOutcomeSchema = z.object({
  reviewId: idSchema,
  status: z.enum(['created', 'skipped', 'blocked', 'failed']),
  transactionId: idSchema.nullable(),
  reason: z.string().min(1).max(300).nullable(),
}).strict();

export const approveOpenFinanceReviewResponseSchema = z.object({
  idempotentReplay: z.boolean(),
  outcome: approvalOutcomeSchema,
}).strict();
export type ApproveOpenFinanceReviewResponse = z.infer<typeof approveOpenFinanceReviewResponseSchema>;

export const approveOpenFinanceSafeBodySchema = z.object({
  idempotencyKey: z.string().trim().min(8).max(120),
  reviewIds: z.array(idSchema).min(1).max(200).superRefine((ids, ctx) => {
    if (new Set(ids).size !== ids.length) ctx.addIssue({ code: 'custom', message: 'reviewIds duplicados.' });
  }),
}).strict();
export type ApproveOpenFinanceSafeBody = z.infer<typeof approveOpenFinanceSafeBodySchema>;

export const approveOpenFinanceSafeResponseSchema = z.object({
  idempotentReplay: z.boolean(),
  summary: z.object({
    selected: z.number().int().nonnegative(),
    created: z.number().int().nonnegative(),
    skipped: z.number().int().nonnegative(),
    blocked: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
  }).strict(),
  outcomes: z.array(approvalOutcomeSchema).max(200),
}).strict();
export type ApproveOpenFinanceSafeResponse = z.infer<typeof approveOpenFinanceSafeResponseSchema>;

const scheduleTimeSchema = z.string().regex(/^(?:[01]\d|2[0-3]):[0-5]\d$/);
const timezoneSchema = z.string().min(1).max(64).refine((value) => {
  try { new Intl.DateTimeFormat('en-US', { timeZone: value }).format(); return true; }
  catch { return false; }
}, 'Fuso horário inválido.');

export const updateOpenFinanceScheduleBodySchema = z.object({
  timezone: timezoneSchema,
  syncTimes: z.array(scheduleTimeSchema).min(1).max(4).refine((times) => new Set(times).size === times.length, 'Horários duplicados.'),
}).strict();
export type UpdateOpenFinanceScheduleBody = z.infer<typeof updateOpenFinanceScheduleBodySchema>;

export const updateOpenFinanceSourceBodySchema = z.object({ status: z.enum(['active', 'paused']) }).strict();
export type UpdateOpenFinanceSourceBody = z.infer<typeof updateOpenFinanceSourceBodySchema>;

export const openFinanceSettingsResponseSchema = z.object({
  provider: z.object({ name: z.literal('belvo'), newConnectionsAvailable: z.boolean(), pilotActive: z.boolean() }).strict(),
  connections: z.array(z.object({
    id: idSchema,
    institutionName: z.string().min(1).max(120),
    status: z.enum(['pending_auth', 'active', 'refreshing', 'attention_required', 'consent_expired', 'disconnected']),
    timezone: timezoneSchema,
    syncTimes: z.array(scheduleTimeSchema).min(1).max(4),
    lastSuccessfulSyncAt: isoTimestampSchema.nullable(),
    sources: z.array(z.object({
      id: idSchema,
      displayName: z.string().min(1).max(120),
      kind: z.enum(['account', 'card']),
      status: z.enum(['active', 'paused', 'attention_required', 'disconnected']),
      mappedName: z.string().min(1).max(120).nullable(),
      lastSuccessfulSyncAt: isoTimestampSchema.nullable(),
    }).strict()).max(200),
  }).strict()).max(200),
}).strict();
export type OpenFinanceSettingsResponse = z.infer<typeof openFinanceSettingsResponseSchema>;
